import { useCallback, useEffect, useRef, useState } from 'react'
import { useLibraryDeps } from '../LibraryDeps'
import type { VaultItem } from './vaultData'

/** How much of a note a grid tile shows; the viewer shows all of it. */
export const NOTE_TILE_CHARS = 80
/** Matches the backend cap (vault_notes.MAX_NOTES_LEN). */
export const NOTE_MAX_CHARS = 5000

export function notePreview(note: string, max = NOTE_TILE_CHARS): string {
  const flat = note.replace(/\s+/g, ' ').trim()
  return flat.length > max ? `${flat.slice(0, max).trimEnd()}…` : flat
}

/**
 * Operator notes and Badges for the loaded Vault items: one load per newly shown page
 * of items (never per tile), plus a save that updates the shown note in place.
 */
export function useVaultNotes(items: VaultItem[]) {
  const { notes: api } = useLibraryDeps()
  const [notes, setNotes] = useState<Record<number, string>>({})
  const [badges, setBadges] = useState<Record<number, number[]>>({})
  const asked = useRef(new Set<number>())
  // Another account = other notes: forget what was loaded for the previous one.
  const current = useRef(api)
  if (current.current !== api) {
    current.current = api
    asked.current = new Set()
    setNotes({})
    setBadges({})
  }

  useEffect(() => {
    if (!api) return
    const fresh = items.map((m) => m.id).filter((id) => !asked.current.has(id))
    if (!fresh.length) return
    fresh.forEach((id) => asked.current.add(id))
    // No per-run cancel: the next page often lands before this answer, and its
    // effect run must not drop it. Only a switch of account discards it.
    api
      .load(fresh.map(String))
      .then((got) => {
        if (current.current !== api) return
        const add: Record<number, string> = {}
        for (const [id, text] of Object.entries(got.notes)) add[Number(id)] = text
        setNotes((n) => ({ ...n, ...add }))
        const tagged: Record<number, number[]> = {}
        for (const [id, ids] of Object.entries(got.badges ?? {})) tagged[Number(id)] = ids
        if (Object.keys(tagged).length) setBadges((b) => ({ ...b, ...tagged }))
      })
      // Not loaded: ask again the next time these items are shown.
      .catch(() => fresh.forEach((id) => asked.current.delete(id)))
  }, [api, items])

  const save = useCallback(
    async (id: number, text: string) => {
      if (!api) return
      const stored = await api.save(String(id), text)
      setNotes((n) => {
        const next = { ...n }
        if (stored) next[id] = stored
        else delete next[id]
        return next
      })
    },
    [api],
  )

  return { notes, save: api ? save : undefined, badges, setBadges }
}
